import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap"; // Keeping Spinner for simplicity
import { FaGooglePlay, FaExternalLinkAlt, FaChartLine } from "react-icons/fa"; 
import CountryList from "../CountryList";
import { googleApi } from "../../services/api";

const Overview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState("");
  const [category, setCategory] = useState("game"); // Default category
  const [selectedDate, setSelectedDate] = useState(new Date());

  const calculateDaysAgo = (date) => {
    const today = new Date();
    const selected = new Date(date);
    const diffTime = today - selected;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)); 
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const daysAgo = calculateDaysAgo(selectedDate);
        const { data: result } = await googleApi.get("fetch-rankings/", {
          params: { country, category, days_ago: daysAgo },
        });
        setData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [country, category, selectedDate]);

  const renderAppsSection = (title, apps) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full">
       <div className="p-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-bold text-gray-800">{title}</h3>
       </div>
       <div className="p-4 overflow-y-auto max-h-[600px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {apps.map((app) => (
            <a 
                key={app.rank} 
                href={app.url || "#"} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center p-3 mb-3 bg-white border border-gray-100 rounded-lg hover:shadow-md transition-all duration-200 group"
            >
                <img 
                    src={app.icon} 
                    alt={app.name} 
                    className="w-12 h-12 rounded-xl object-cover shadow-sm mr-4 group-hover:scale-105 transition-transform"
                />
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-gray-800 truncate group-hover:text-blue-600 transition-colors">{app.name}</h4>
                    <p className="text-xs text-gray-500 truncate">{app.developer}</p>
                    <div className="flex items-center mt-1 space-x-2">
                        <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">#{app.rank}</span>
                        {app.rating && <span className="text-xs text-yellow-500">★ {app.rating.toFixed(1)}</span>}
                    </div>
                </div>
                <FaExternalLinkAlt className="text-gray-300 text-xs group-hover:text-blue-500" />
            </a>
          ))}
       </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-900 to-green-700 rounded-2xl p-8 mb-8 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10">
            <h1 className="text-3xl font-bold mb-2 flex items-center">
                <FaGooglePlay className="mr-3 text-4xl text-green-300" />
                Market Overview
            </h1>
            <p className="text-green-100 max-w-2xl">
                Explore top rankings across different categories and countries on the Google Play Store.
            </p>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 bg-white/5 skew-x-12 transform translate-x-1/2"></div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        <div className="md:col-span-4">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 h-full">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Select Country</label>
                <CountryList onSelect={(selectedCountry) => setCountry(selectedCountry)} />
             </div>
        </div>

        <div className="md:col-span-4">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 h-full">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Select Category</label>
                 <select 
                    className="w-full bg-gray-50 border border-gray-200 text-gray-700 py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                 >
                    <option value="game">Games</option>
                    <option value="art_and_design">Art & Design</option>
                    <option value="auto_and_vehicles">Auto & Vehicles</option>
                    <option value="beauty">Beauty</option>
                    <option value="books_and_reference">Books & Reference</option>
                    <option value="business">Business</option>
                    <option value="comics">Comics</option>
                    <option value="communication">Communication</option>
                    <option value="dating">Dating</option>
                    <option value="education">Education</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="finance">Finance</option>
                    <option value="food_and_drink">Food & Drink</option>
                    <option value="health_and_fitness">Health & Fitness</option>
                    <option value="lifestyle">Lifestyle</option>
                    <option value="maps_and_navigation">Maps & Navigation</option>
                    <option value="medical">Medical</option>
                    <option value="music_and_audio">Music & Audio</option>
                    <option value="news_and_magazines">News & Magazines</option>
                    <option value="parenting">Parenting</option>
                    <option value="personalization">Personalization</option>
                    <option value="photography">Photography</option>
                    <option value="productivity">Productivity</option>
                    <option value="shopping">Shopping</option>
                    <option value="social">Social</option>
                    <option value="sports">Sports</option>
                    <option value="tools">Tools</option>
                    <option value="travel_and_local">Travel & Local</option>
                    <option value="video_players">Video Players & Editors</option>
                    <option value="weather">Weather</option>
                 </select>
             </div>
        </div>

        <div className="md:col-span-4">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 h-full">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Select Date</label>
                 <input 
                    type="date"
                    className="w-full bg-gray-50 border border-gray-200 text-gray-700 py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={selectedDate.toISOString().split("T")[0]}
                    onChange={(e) => setSelectedDate(new Date(e.target.value))}
                 />
             </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <span className="bg-green-100 text-green-700 p-2 rounded-lg mr-3">
                <FaChartLine />
            </span>
            Top Charts
        </h2>
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center py-20">
          <Spinner animation="border" variant="success" className="mb-4" />
          <p className="text-gray-500 animate-pulse">Fetching ranking data...</p>
        </div>
      ) : (
        data && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
             {renderAppsSection("Top Free Apps", data.top_free_apps)}
             {renderAppsSection("Top Paid Apps", data.top_paid_apps)}
             {renderAppsSection("Top Grossing Apps", data.top_grossing_apps)}
          </div>
        )
      )}
    </div>
  );
};

export default Overview;
